import { useEffect, useState, useRef } from "react";
import { api, apiErr, fileUrl } from "@/admin/api";
import { PageTitle } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, ImagePlus, MapPin } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Resort Stay", "Cruise", "Group Travel", "Travel Essential", "Seasonal Offer"];
const EMPTY = { image_url: "", category: "Resort Stay", location: "", property_name: "", caption: "", status: "Published" };

export default function GalleryManager() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try { const { data } = await api.get("/admin/gallery"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditingId(it.id); setOpen(true); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Photo uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.image_url) { toast.error("Please upload a photo first."); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/gallery/${editingId}`, form);
      else await api.post("/admin/gallery", form);
      toast.success(editingId ? "Photo updated" : "Photo added to gallery");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try { await api.delete(`/admin/gallery/${it.id}`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-gallery">
      <PageTitle title="Travel Gallery" subtitle="Showcase photos from past adventures on your homepage">
        <Button data-testid="new-gallery" onClick={openNew} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
          <Plus size={16} /> Add Photo
        </Button>
      </PageTitle>

      {!items ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-bone p-16 text-center">
          <p className="text-ink-soft">No photos yet. Add your first travel memory.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`gallery-card-${it.id}`} className="bg-white rounded-2xl border border-bone overflow-hidden shadow-sm">
              <div className="h-44 bg-bone">
                <img src={fileUrl(it.image_url)} alt={it.property_name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <span className="text-[11px] uppercase tracking-wide text-terracotta font-semibold">{it.category}</span>
                <p className="font-medium text-ink text-sm mt-1 truncate">{it.property_name || "—"}</p>
                {it.location && <p className="text-xs text-ink-soft flex items-center gap-1 truncate"><MapPin size={11} /> {it.location}</p>}
                <div className="flex gap-1 mt-2">
                  <Button data-testid={`gallery-edit-${it.id}`} onClick={() => openEdit(it)} variant="ghost" size="sm" className="rounded-lg text-ink-soft text-xs h-8 px-2">Edit</Button>
                  <Button data-testid={`gallery-delete-${it.id}`} onClick={() => remove(it)} variant="ghost" size="icon" className="rounded-lg text-red-500 hover:bg-red-50 ml-auto h-8 w-8"><Trash2 size={15} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sand">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editingId ? "Edit Photo" : "Add Travel Photo"}</DialogTitle>
            <DialogDescription className="text-ink-soft text-sm">Upload a photo and tag it with a category, location, and property name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-ink font-medium">Photo</Label>
              <div className="mt-2 h-40 rounded-xl overflow-hidden bg-bone flex items-center justify-center">
                {form.image_url
                  ? <img src={fileUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                  : <ImagePlus size={32} className="text-ink-soft/40" />}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gallery-image-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-2 rounded-full border-ink/20 gap-2">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Upload
              </Button>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Travel Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="gallery-category" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label className="text-ink font-medium">Location</Label>
                <Input data-testid="gallery-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Cancún, Mexico" className="rounded-xl border-bone bg-white" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-ink font-medium">Property Name</Label>
                <Input data-testid="gallery-property" value={form.property_name} onChange={(e) => set("property_name", e.target.value)} placeholder="e.g. Riu Palace" className="rounded-xl border-bone bg-white" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Caption (optional)</Label>
              <Input value={form.caption} onChange={(e) => set("caption", e.target.value)} className="rounded-xl border-bone bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="gallery-save" onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editingId ? "Save Photo" : "Add to Gallery")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
